/* eslint-disable react/no-array-index-key */
import * as React from 'react';

import Group, { GroupAddon } from '@lightspeed/cirrus-group';
import { Bone } from '@lightspeed/flame/Bone';
import { Card, CardSection } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { Divider } from '@lightspeed/flame/Divider';
import { Text } from '@lightspeed/flame/Text';
import isNil from 'lodash/isNil';

import SectionTitle from 'root/apps/PurchaseOrder/ShipmentHistory/components/SectionTitle';
import OverviewTooltip from 'root/apps/PurchaseOrder/ShipmentHistory/tabs/View/components/OverviewTooltip';
import { OverviewDataContext } from 'root/apps/PurchaseOrder/ShipmentHistory/tabs/View/contexts/OverviewData';
import { DiscountMethod, ShippingCostMethod } from 'root/apps/PurchaseOrder/ShipmentHistory/types';
import { formatCurrency } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/format';
import TooltipPurchaseOrderShippingCost from 'root/apps/Tooltip/PurchaseOrderShippingCost';

import ShippingCostInput from './ShippingCostInput';

interface Props {
  isMultiCurrency: boolean;
}

const Overview: React.FC<Props> = ({ isMultiCurrency }) => {
  const { data, isLoading, isInitialLoading, isDisabled, hasPurchaseOrderLandedCostsSpreadCosts } =
    React.useContext(OverviewDataContext);
  const { currency_code: currencyCode } = window.merchantos.locale;
  const tdWidthTotalSubtotal = isMultiCurrency ? '25%' : '50%';
  const tdWidthShippingDiscount = isMultiCurrency ? '25%' : '33.3333%';

  const disableEditingShippingMethods: ShippingCostMethod[] = ['evenly', 'proportionally'];
  const disableShippingCostFields = !!(
    isDisabled ||
    (!hasPurchaseOrderLandedCostsSpreadCosts && data?.shippingCostSource === 'order') ||
    (data?.shippingCostMethod && disableEditingShippingMethods.includes(data.shippingCostMethod))
  );

  const methodNameChange = (method: ShippingCostMethod | DiscountMethod) => {
    switch (method) {
      case 'total':
        return 'Don’t apply to items';
      case 'evenly':
        return 'Split evenly based on item qty';
      case 'proportionally':
        return 'Split based on vendor cost';
      case 'manually':
        return 'Apply manually to items';
      default:
        return method;
    }
  };

  const formatMoneyValueForDisplay = (value: number, currency: string): string => {
    return !isNil(value) || !isNil(currency) ? formatCurrency(value, currency) : '-';
  };

  const costMethodGenericTooltipContent = (
    <>
      <i>Split evenly based on item quantity, split based on vendor cost</i> or{' '}
      <i>apply manually to items</i> was selected on the PO and is applied to this shipment
    </>
  );

  const applyManuallyMethodTooltipContent = (
    <>
      <i>Split evenly based on item quantity</i> or <i>split based on vendor cost</i> was selected
      on the PO and is applied to this shipment
    </>
  );

  const shippingCostTooltipContent =
    data?.shippingCostMethod && ['evenly', 'proportionally'].includes(data.shippingCostMethod)
      ? costMethodGenericTooltipContent
      : undefined;

  const discountTooltipContent =
    data?.discountMethod && ['manually'].includes(data.discountMethod)
      ? applyManuallyMethodTooltipContent
      : costMethodGenericTooltipContent;

  return (
    <Box style={{ marginBottom: '36px', width: '100%' }}>
      <Flex justifyContent="space-between">
        {!isInitialLoading ? (
          <SectionTitle>Total cost overview</SectionTitle>
        ) : (
          <Box>
            <Bone height={4} width="150px" data-test="cost-overview-loading" />
          </Box>
        )}
        <Card style={{ flex: 2, maxWidth: '60%' }}>
          {isInitialLoading || !data ? (
            <>
              <CardSection>
                <Flex justifyContent="flex-end">
                  <Bone height={3} width="50%" />
                </Flex>
              </CardSection>
              <Divider />
              {new Array(3).fill(0).map((_v, index, row) => (
                <React.Fragment key={index}>
                  <CardSection style={{ padding: '1rem' }}>
                    <Flex justifyContent="space-between">
                      <Bone height={3} width="25%" />
                      <Bone height={3} width="25%" />
                    </Flex>
                  </CardSection>
                  {index + 1 !== row.length ? <Divider /> : null}
                </React.Fragment>
              ))}
            </>
          ) : null}

          {!isInitialLoading && data ? (
            <>
              {hasPurchaseOrderLandedCostsSpreadCosts ? (
                <>
                  {isMultiCurrency ? (
                    <>
                      <CardSection>
                        <Flex justifyContent="flex-end">
                          <Text
                            width="25%"
                            textAlign="end"
                            fontWeight="bold"
                            style={{ opacity: isLoading ? 0.5 : 1 }}
                          >
                            Total <span className="notranslate">({data?.vendorCurrencyCode})</span>
                          </Text>
                          <Text
                            width={`${isMultiCurrency ? 25 : 25 * 2}%`}
                            textAlign="end"
                            fontWeight="bold"
                            style={{ opacity: isLoading ? 0.5 : 1 }}
                          >
                            Total <span className="notranslate">({currencyCode})</span>
                          </Text>
                        </Flex>
                      </CardSection>
                      <Divider />
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  {/* TO DO: Previous flow to be removed once land cost feature deploys */}
                  <CardSection>
                    <Flex justifyContent="flex-end">
                      {isMultiCurrency ? (
                        <Text
                          width="25%"
                          textAlign="end"
                          fontWeight="bold"
                          style={{ opacity: isLoading ? 0.5 : 1 }}
                        >
                          Total <span className="notranslate">({data?.vendorCurrencyCode})</span>
                        </Text>
                      ) : null}
                      <Text
                        width={`${isMultiCurrency ? 25 : 25 * 2}%`}
                        textAlign="end"
                        fontWeight="bold"
                        style={{ opacity: isLoading ? 0.5 : 1 }}
                      >
                        Total{' '}
                        {isMultiCurrency ? (
                          <span className="notranslate">({currencyCode})</span>
                        ) : (
                          ''
                        )}
                      </Text>
                    </Flex>
                  </CardSection>
                  <Divider />
                </>
              )}
              <CardSection style={{ padding: '1rem' }}>
                <Flex>
                  <Text width="50%" fontWeight="bold">
                    Subtotal
                  </Text>
                  {isMultiCurrency ? (
                    <Text
                      width={tdWidthTotalSubtotal}
                      textAlign="end"
                      className="notranslate"
                      style={{ opacity: isLoading ? 0.5 : 1 }}
                    >
                      {formatMoneyValueForDisplay(
                        data?.sumTotalReceivedVendorCurrency,
                        data.vendorCurrencyCode
                      )}
                    </Text>
                  ) : null}
                  <Text
                    width={tdWidthTotalSubtotal}
                    textAlign="end"
                    className="notranslate"
                    style={{ opacity: isLoading ? 0.5 : 1 }}
                  >
                    {formatMoneyValueForDisplay(data?.sumTotalReceived, data.defaultCurrencyCode)}
                  </Text>
                </Flex>
              </CardSection>
              <Divider />
              {hasPurchaseOrderLandedCostsSpreadCosts ? (
                <>
                  <CardSection style={{ padding: '6px 1rem' }}>
                    <Flex>
                      <Flex width={tdWidthShippingDiscount} alignItems="center" fontWeight="bold">
                        Shipping cost
                      </Flex>
                      <Flex
                        width={tdWidthShippingDiscount}
                        alignItems="center"
                        justifyContent="start"
                        fontWeight="bold"
                        data-test="cost-overview-shipping-method"
                      >
                        {methodNameChange(data.shippingCostMethod)}
                      </Flex>
                      {data.shippingCostMethod === 'total' ? (
                        <>
                          {isMultiCurrency ? (
                            <>
                              <Flex
                                width={tdWidthTotalSubtotal}
                                alignItems="flex-end"
                                flexDirection="column"
                              >
                                <Group noSpacing inputBlock>
                                  <GroupAddon>{data.vendorCurrencySymbol}</GroupAddon>
                                  <ShippingCostInput
                                    value={data.shippingCostVendorCurrency || '0.00'}
                                    name="shippingVendorCost"
                                    orderId={data.orderID}
                                    shipmentId={data.shipmentID}
                                    isDisabled={disableShippingCostFields}
                                    style={{ textAlign: 'right', width: 68 }}
                                    data-test="cost-overview-shipping-cost-vendor-currency-input"
                                  />
                                </Group>
                              </Flex>
                              <Flex
                                width={tdWidthTotalSubtotal}
                                alignItems="flex-end"
                                flexDirection="column"
                              >
                                <Group noSpacing inputBlock>
                                  <GroupAddon>{data.defaultCurrencySymbol}</GroupAddon>
                                  <ShippingCostInput
                                    value={data.shippingCost || '0.00'}
                                    name="shippingCost"
                                    orderId={data.orderID}
                                    shipmentId={data.shipmentID}
                                    isDisabled={disableShippingCostFields}
                                    style={{ textAlign: 'right', width: 68 }}
                                    data-test="cost-overview-shipping-cost-input"
                                  />
                                </Group>
                              </Flex>
                            </>
                          ) : (
                            <Flex
                              width={tdWidthShippingDiscount}
                              alignItems="flex-end"
                              flexDirection="column"
                            >
                              <Group noSpacing inputBlock>
                                <GroupAddon>{data.vendorCurrencySymbol}</GroupAddon>
                                <ShippingCostInput
                                  value={data.shippingCostVendorCurrency || '0.00'}
                                  name="shippingVendorCost"
                                  orderId={data.orderID}
                                  shipmentId={data.shipmentID}
                                  isDisabled={disableShippingCostFields}
                                  style={{ textAlign: 'right', width: 68 }}
                                  data-test="cost-overview-shipping-cost-input"
                                />
                              </Group>
                            </Flex>
                          )}
                        </>
                      ) : (
                        <>
                          {isMultiCurrency ? (
                            <Flex width="25%" alignItems="flex-end" flexDirection="column">
                              <Flex>
                                <Text data-test="cost-overview-shipping-vendor-currency">
                                  {formatMoneyValueForDisplay(
                                    data?.shippingCostVendorCurrency,
                                    data.vendorCurrencyCode
                                  )}
                                </Text>
                                <OverviewTooltip content={shippingCostTooltipContent} />
                              </Flex>
                              <Text
                                fontSize={12}
                                color={theme.colors['gray-400']}
                                data-test="cost-overview-shipping-cost-order-full-vendor-value"
                              >
                                Cost from PO#<span className="notranslate">{data.orderID}</span>:
                                {formatMoneyValueForDisplay(
                                  data?.shippingCostOrderFullVendorValue,
                                  data.vendorCurrencyCode
                                )}
                              </Text>
                            </Flex>
                          ) : null}
                          <Flex
                            width={tdWidthShippingDiscount}
                            alignItems="flex-end"
                            flexDirection="column"
                          >
                            <Flex>
                              <Text data-test="cost-overview-shipping-cost">
                                {formatMoneyValueForDisplay(
                                  data?.shippingCost,
                                  data.defaultCurrencyCode
                                )}
                              </Text>
                              {!isMultiCurrency ? (
                                <OverviewTooltip
                                  content={shippingCostTooltipContent}
                                  placement="top-end"
                                />
                              ) : null}
                            </Flex>
                            <Text
                              fontSize={12}
                              color={theme.colors['gray-400']}
                              data-test="cost-overview-shipping-cost-order-full-value"
                            >
                              Cost from PO#<span className="notranslate">{data.orderID}</span>:
                              {formatMoneyValueForDisplay(
                                data?.shippingCostOrderFullValue,
                                data.defaultCurrencyCode
                              )}
                            </Text>
                          </Flex>
                        </>
                      )}
                    </Flex>
                  </CardSection>
                  {data.discountMethod !== 'total' ? (
                    <>
                      <Divider />
                      <CardSection style={{ padding: '6px 1rem' }}>
                        <Flex>
                          <Flex
                            width={tdWidthShippingDiscount}
                            alignItems="center"
                            fontWeight="bold"
                          >
                            Discount
                          </Flex>
                          <Flex
                            width={tdWidthShippingDiscount}
                            alignItems="center"
                            justifyContent="start"
                            fontWeight="bold"
                            data-test="cost-overview-discount-method"
                          >
                            {methodNameChange(data.discountMethod)}
                          </Flex>
                          {isMultiCurrency ? (
                            <Flex
                              width={tdWidthShippingDiscount}
                              alignItems="flex-end"
                              flexDirection="column"
                            >
                              <Flex>
                                <Text data-test="cost-overview-discount-money-vendor-value">
                                  {formatMoneyValueForDisplay(
                                    data?.discountMoneyVendorValue,
                                    data.vendorCurrencyCode
                                  )}
                                </Text>
                                <OverviewTooltip content={discountTooltipContent} />
                              </Flex>
                              <Text
                                fontSize={12}
                                color={theme.colors['gray-400']}
                                data-test="cost-overview-discount-order-full-money-vendor-value"
                              >
                                Cost from PO#<span className="notranslate">{data.orderID}</span>:
                                {formatMoneyValueForDisplay(
                                  data?.discountOrderFullMoneyVendorValue,
                                  data.vendorCurrencyCode
                                )}
                              </Text>
                            </Flex>
                          ) : null}
                          <Flex
                            width={tdWidthShippingDiscount}
                            alignItems="flex-end"
                            flexDirection="column"
                          >
                            <Flex>
                              <Text data-test="cost-overview-discount-money-value">
                                {formatMoneyValueForDisplay(
                                  data?.discountMoneyValue,
                                  data.defaultCurrencyCode
                                )}
                              </Text>
                              {!isMultiCurrency ? (
                                <OverviewTooltip
                                  content={discountTooltipContent}
                                  placement="top-end"
                                />
                              ) : null}
                            </Flex>
                            <Text
                              fontSize={12}
                              color={theme.colors['gray-400']}
                              data-test="cost-overview-discount-order-full-money-value"
                            >
                              Cost from PO#<span className="notranslate">{data.orderID}</span>:
                              {formatMoneyValueForDisplay(
                                data?.discountOrderFullMoneyValue,
                                data.defaultCurrencyCode
                              )}
                            </Text>
                          </Flex>
                        </Flex>
                      </CardSection>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <CardSection style={{ padding: '6px 1rem' }}>
                    <Flex>
                      <Flex width="50%" alignItems="center" fontWeight="bold">
                        Shipping cost
                        <TooltipPurchaseOrderShippingCost
                          shippingCostSource={data?.shippingCostSource}
                          location="shipment"
                        />
                      </Flex>
                      {isMultiCurrency ? (
                        <Flex
                          width={tdWidthTotalSubtotal}
                          alignItems="flex-end"
                          flexDirection="column"
                        >
                          <Group noSpacing inputBlock>
                            <GroupAddon>{data.vendorCurrencySymbol}</GroupAddon>
                            <ShippingCostInput
                              value={data.shippingCostVendorCurrency || '0.00'}
                              name="shippingVendorCost"
                              orderId={data.orderID}
                              shipmentId={data.shipmentID}
                              isDisabled={disableShippingCostFields}
                              style={{ textAlign: 'right', width: 68 }}
                            />
                          </Group>
                        </Flex>
                      ) : null}
                      <Flex
                        width={tdWidthTotalSubtotal}
                        alignItems="flex-end"
                        flexDirection="column"
                      >
                        <Group noSpacing inputBlock>
                          <GroupAddon>{data.defaultCurrencySymbol}</GroupAddon>
                          <ShippingCostInput
                            value={data.shippingCost || '0.00'}
                            name="shippingCost"
                            orderId={data.orderID}
                            shipmentId={data.shipmentID}
                            isDisabled={disableShippingCostFields}
                            style={{ textAlign: 'right', width: 68 }}
                          />
                        </Group>
                      </Flex>
                    </Flex>
                  </CardSection>
                </>
              )}
              <Divider />
              <CardSection style={{ padding: '1rem' }}>
                <Flex>
                  <Text width="50%" fontWeight="bold">
                    Total
                  </Text>
                  {isMultiCurrency ? (
                    <Text
                      width={tdWidthTotalSubtotal}
                      textAlign="end"
                      className="notranslate"
                      style={{ opacity: isLoading ? 0.5 : 1 }}
                    >
                      {formatMoneyValueForDisplay(
                        data?.sumTotalCostsVendorCurrency,
                        data?.vendorCurrencyCode
                      )}
                    </Text>
                  ) : null}
                  <Text
                    width={tdWidthTotalSubtotal}
                    textAlign="end"
                    className="notranslate"
                    style={{ opacity: isLoading ? 0.5 : 1 }}
                  >
                    {formatMoneyValueForDisplay(data?.sumTotalCosts, data.defaultCurrencyCode)}
                  </Text>
                </Flex>
              </CardSection>
            </>
          ) : null}
        </Card>
      </Flex>
    </Box>
  );
};

export default Overview;
